/* global window */

import $ from 'jquery';
import {curry, flattenDeep, isPlainObject} from 'lodash';
import {Record} from 'immutable';
import {reportBug} from '@packages/utilities/reporter';
import {API_ERROR_MESSAGES} from '@packages/systems/cms/constants';
import {getDesignerSessionId} from '@packages/systems/multiplayer/session';
import {
  getAttributionHeaders,
  initDesignerAttribution,
} from '@packages/systems/designer-attribution';
// Handle SSR environments where window is undefined
const {fetch, FormData, XMLHttpRequest} =
  typeof window !== 'undefined' ? window : {};
import {SERVER_ERROR_RECEIVED} from '@packages/systems/designer/actions';

let flux;
let config = Record(
  {
    host: '',
    mode: 'no-cors',
    credentials: 'same-origin',
    publicFetch: false,
  },
  'ClientUtilsConfig'
)();

export function getDefaultHeaders() {
  const csrfToken = $('meta[name=_csrf]').attr('content');
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'X-XSRF-Token': String(csrfToken),
  };

  const sessionId = getDesignerSessionId();
  if (sessionId) {
    headers['X-Session-Id'] = sessionId;
  }

  Object.assign(headers, getAttributionHeaders());

  return headers;
}

export function initialize(_flux) {
  flux = _flux;
  initDesignerAttribution(_flux);
}
export function configure(options) {
  config = config.merge(options);
}

export function request(
  path,
  {headers = {}, responseHandler = handleJSON, ...rest} = {}
) {
  const {host, credentials} = config;

  const options = {
    headers: {
      ...getDefaultHeaders(),
      ...headers,
    },
    credentials,
    ...rest,
  };

  if (host) path = flattenDeep([host, path]);

  path = cleanSlashes(makePath(path));

  if (
    options.query &&
    isPlainObject(options.query) &&
    Object.keys(options.query).length
  ) {
    const query = new URLSearchParams(options.query);
    path = path.concat(`?${query.toString()}`);
  }

  return fetch(path, options).then(responseHandler);
}

export function getFetchOptions() {
  return {
    host: '',
    headers: getDefaultHeaders(),
    credentials: 'include',
  };
}

export function makeEndpoint({path: base}) {
  return {index, get, patch, put, post, delete: _delete};

  function index(path) {
    return request([base, path]);
  }

  function get(path, options = {}) {
    return request([base, path], {
      ...options,
      method: 'GET',
    });
  }

  function patch(path, obj, options = {}) {
    return request([base, path], {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(obj),
    });
  }

  function put(path, obj, options = {}) {
    return request([base, path], {
      ...options,
      method: 'PUT',
      body: JSON.stringify(obj),
    });
  }

  function post(path, obj, options = {}) {
    return request([base, path], {
      ...options,
      method: 'POST',
      body: JSON.stringify(obj),
    });
  }

  function _delete(path, obj, options = {}) {
    return request([base, path], {
      ...options,
      method: 'DELETE',
      body: obj ? JSON.stringify(obj) : null,
    });
  }
}

const INCOMPATIBLE_CLIENT_VERSION = 'Incompatible client version';

// These are errors which we know are generally
// user error and expected. Thus, we don't need
// to report them.
const HANDLED_ERROR_MESSAGES = [
  API_ERROR_MESSAGES.COLLECTION_FIELDS_USED,
  INCOMPATIBLE_CLIENT_VERSION,
];

export function handleError(message) {
  return !flux
    ? null
    : (response) => {
        if (isOutOfAiCreditsError(response)) {
          return Promise.reject(response);
        }

        if (
          response.code === 400 &&
          response.msg === INCOMPATIBLE_CLIENT_VERSION
        ) {
          flux
            .__DEPRECATED__getCreators('__DEPRECATED__ConfirmActionCreators')
            .confirmStaleClient(
              'Refresh Your Browser',
              'Refresh your browser to enjoy the latest and greatest version of Webflow'
            );
        }

        const errorMessage = response.msg || response.err || message;

        flux.dispatch({
          type: SERVER_ERROR_RECEIVED,
          errorMessage,
          errorObject: response,
        });

        // If the error message is not included within the handled error messages
        // array then we don't report to bugnsag.
        // Note: If we had deterministic IDs for errors then we would be able to
        // use the IDs directly to identify handled (expected) errors.
        if (!HANDLED_ERROR_MESSAGES.some((msg) => errorMessage.includes(msg))) {
          // Please see https://github.com/webflow/webflow/pull/39900
          // eslint-disable-next-line webflow/better-bug-reporting
          reportBug('UnhandledServerApiError', errorMessage, {
            code: response.code,
            detail: response.err,
          });
        }

        return Promise.reject(response);
      };
}

export function logError(response) {
  console.error(response.msg || response.err);
  return Promise.reject(response);
}

export function makeFormData(obj) {
  const data = new FormData();
  for (const k in obj) {
    if (Array.isArray(obj[k])) {
      obj[k].forEach((item) => data.append(k, item));
    } else {
      data.append(k, obj[k]);
    }
  }
  return data;
}

export function makePath(p) {
  if (typeof p === 'string') {
    return p;
  } else if (Array.isArray(p)) {
    return p.filter(isNotEmpty).map(makePath).join('/');
  } else {
    return '?' + makeQuery(p);
  }
}

export function makeQuery(o) {
  return flattenDeep(
    Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .map((k) => makeQueryPart(k, o[k]))
  ).join('&');
}

const makeQueryPart = curry((k, v) => {
  return Array.isArray(v)
    ? v.map(makeQueryPart(k + '[]'))
    : [k, v].map(encodeURIComponent).join('=');
});

export function when(errorClass, fn) {
  return (error) =>
    error.type === errorClass.name ? fn && fn(error) : Promise.reject(error);
}

export function notifyWhen(errorClass, notifier) {
  return when(errorClass, (e) => {
    notifier(e.message);
    return Promise.reject(e);
  });
}

export function xhr(options) {
  const {
    method,
    path,
    host,
    headers = {},
    body,
    onUploadProgress,
    withCredentials,
    responseType,
    cors,
    signal,
  } = options;

  const _headers = {
    'X-Requested-With': 'XMLHttpRequest',
    ...(cors ? {} : getAttributionHeaders()),
    ...headers,
  };

  const url = cleanSlashes(makePath([host, path]));

  const _xhr = new XMLHttpRequest();
  _xhr.withCredentials = withCredentials;

  _xhr.open(method, url, 'async');

  if (onUploadProgress) {
    _xhr.upload.addEventListener('progress', onUploadProgress, !'capture');
  }

  if (cors) {
    // eslint-disable-next-line webflow/no-delete
    delete _headers['X-Requested-With'];
  }

  // add headers if it has some value
  for (const key in _headers) _xhr.setRequestHeader(key, _headers[key]);

  if (responseType) {
    _xhr.responseType = responseType;
  }

  return new Promise((resolve, reject) => {
    let abortHandler = null;
    let settled = false;
    let didAbort = false;

    const settleResolve = (v) => {
      if (settled) return;
      settled = true;
      if (signal && abortHandler) {
        signal.removeEventListener('abort', abortHandler);
      }
      resolve(v);
    };
    const settleReject = (e) => {
      if (settled) return;
      settled = true;
      if (signal && abortHandler) {
        signal.removeEventListener('abort', abortHandler);
      }
      reject(e);
    };

    // Set up abort handling if a signal is provided
    if (signal) {
      if (signal.aborted) {
        settleReject(
          signal.reason ||
            new DOMException('The operation was aborted.', 'AbortError')
        );
        return;
      }

      _xhr.onabort = () => {
        didAbort = true;
        settleReject(
          signal.reason ||
            new DOMException('The operation was aborted.', 'AbortError')
        );
      };

      abortHandler = () => {
        didAbort = true; // Set before abort() to prevent onreadystatechange from resolving
        _xhr.abort();
      };

      signal.addEventListener('abort', abortHandler, {once: true});
    }

    _xhr.onreadystatechange = function onReadyStateChange() {
      if (_xhr.readyState === _xhr.DONE) {
        if (didAbort) return; // ignore DONE caused by abort
        settleResolve(_xhr);
      }
    };
    _xhr.send(body);
  }).then(rejectXHRErrors);
}

export function handleJSON(r) {
  return r.json().then(rejectFetchErrors(r)).catch(refreshIfLoggedOut);
}

/**
 * For endpoints that answer 204, whose body is empty by definition.
 *
 * Pass as `responseHandler` rather than teaching `handleJSON` about 204:
 * every caller that does not opt in keeps rejecting on an unreadable body,
 * which is the behavior their error paths were written against.
 */
export function handleEmptyOrJSON(r) {
  return r.status === 204 ? Promise.resolve(undefined) : handleJSON(r);
}

function rejectFetchErrors(r) {
  return (json) => (r.ok ? json : Promise.reject(json));
}

function rejectXHRErrors(r) {
  return r.status >= 200 && r.status < 300 ? r : Promise.reject(r);
}

function isOutOfAiCreditsError(r) {
  return (
    r?.code === 403 &&
    (r.name === 'LimitReachedError' ||
      r.errorEnum === 'LimitReached' ||
      r.meta?.code === 'LimitReached') &&
    /ai credits/i.test(r.msg || r.message || '')
  );
}

function refreshIfLoggedOut(r) {
  if (isOutOfAiCreditsError(r)) {
    return Promise.reject(r);
  }

  if (r.code === 401 || r.code === 403) {
    refreshPage(r.msg);
    // window.location.reload() schedules a navigation but does not synchronously
    // tear down JS execution. Returning a never-settling promise prevents the
    // outer chain from resolving with `undefined` and crashing downstream
    // `.then` handlers (e.g. getSiteUsers' `[...r.designers]`) before the page
    // actually unloads.
    return new Promise(() => {});
  }
  return Promise.reject(r);
}

function refreshPage(msg) {
  window.alert(msg || "You've been logged out. Sign back in to continue.");
  window.location.reload();
}

function isNotEmpty(s) {
  return Boolean(s);
}

function cleanSlashes(url) {
  // Prevent `endpoint/?` redirect issue with cors
  url = url.replace('/?', '?');
  // Replace multiple slashes with single slashes
  return url.replace(/([^:])\/\/+/g, '$1/');
}
